/**
 * 상담 채팅 위젯의 동작 — 디자인1·블랙·루미나 위젯이 함께 쓴다(화면만 틀마다 다르다). 세 틀에 **같은 파일**을 둔다.
 *
 * 노출: 서버 판정(마스터 on ∧ 이 도메인 디자인 토글 on)일 때만 버튼 표시. 비로그인 클릭 → 로그인 이동.
 * 실시간: 소켓 chat:* 이벤트(window CustomEvent 경유) + 창 열림 중 15초 폴백 재조회 + 재연결 시 재조회
 *         (통지 유실 대비 — REST 가 정본). 메시지는 클라이언트 uuid 로 병합해 중복 표시를 막는다.
 * 표시: 상대(관리인) 이름은 채팅 설정의 닉네임. 종료된 대화는 유저에게 보이지 않는다(내역은 관리자 전용).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import useAuth from './useAuth';

const genUuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    }));

// 서버가 채팅이 꺼졌음을 알리는 판정 값(user-api routes/chat.js)
const CHAT_DISABLED_CODE = 'CHAT_DISABLED';
const POLL_INTERVAL_MS = 15000;
// 첨부 올리기는 일반 요청 제한(60초)보다 오래 걸린다 — 허용 크기까지의 파일이 느린 회선에서도 끝나도록 nginx /api 대기 한도(300초)에 맞춘다
const UPLOAD_TIMEOUT_MS = 300000;
// 맨 아래에서 이만큼 안쪽이면 '맨 아래를 보는 중'으로 친다(말풍선 여백 때문에 정확히 0 이 아닐 수 있다)
const NEAR_BOTTOM_PX = 40;

// 숨은 탭은 읽음을 보내지 않는다 — 회원이 아직 안 본 글이고, 읽은 시각은 상담원 화면의 읽음 표시로 나간다(다시 보일 때 보낸다)
const isPageVisible = () => document.visibilityState === 'visible';

// 재조회 결과가 같으면 새 배열로 바꾸지 않는다 — 바꾸면 화면이 다시 그려지고, 종전엔 15초마다 맨 아래로 끌려갔다(2026-11-01 점검)
const isSameList = (a, b) => a.length === b.length
  && a.every((m, i) => m.id === b[i].id && m.read_at === b[i].read_at);

/**
 * 첨부 미리 검사 — 전송을 누르기 전에 알려 준다. 규칙을 못 받았으면(null) 서버 검사에 맡긴다.
 * @returns 거절 사유 문구, 통과면 null
 */
const checkAttachment = (file, rules) => {
  if (!rules) return null;
  const ext = (file.name.match(/\.[^.]+$/)?.[0] || '').toLowerCase();
  if (!rules.extensions.includes(ext)) return rules.typeMessage;
  if (file.size > rules.maxBytes) return rules.sizeMessage;
  return null;
};

const useChatWidget = () => {
  const { user } = useAuth();
  const [available, setAvailable] = useState(false);
  const [attachmentRules, setAttachmentRules] = useState(null);
  const [open, setOpen] = useState(false);
  const [agentNickname, setAgentNickname] = useState('상담원');
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [closedNotice, setClosedNotice] = useState(false);
  const [hasNewBelow, setHasNewBelow] = useState(false);

  const openRef = useRef(false); openRef.current = open;
  const userRef = useRef(user); userRef.current = user;
  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const shownCountRef = useRef(0);
  const hadConversationRef = useRef(false);
  // 보내다 실패한 글의 번호·올린 첨부 — 다시 보낼 때 그대로 써서, 응답만 잃은 경우 서버가 같은 글로 알아본다(새 번호면 두 번 저장된다)
  const draftRef = useRef(null);

  const hideChat = useCallback(() => {
    setAvailable(false);
    setOpen(false);
  }, []);

  // ── 노출 판정 (마스터 ∧ 디자인 — 서버가 판정) ──
  useEffect(() => {
    let alive = true;
    api.get('/chat/availability')
      .then(res => {
        if (!alive) return;
        setAvailable(!!res.data?.available);
        setAttachmentRules(res.data?.attachmentRules || null);
      })
      .catch(() => { if (alive) setAvailable(false); });
    return () => { alive = false; };
  }, []);

  // 상담이 끝나면 대화가 비므로 "새 메시지 보기"가 가리킬 글이 없다 — 표시를 지우고, 다음 상담은 맨 아래를 보는 상태로 시작한다
  const resetScrollMark = useCallback(() => {
    stickToBottomRef.current = true;
    setHasNewBelow(false);
  }, []);

  // ── 대화 재조회 (정본 — 열기/재연결/폴백 폴링에서 호출) ──
  const refresh = useCallback(async () => {
    if (!userRef.current) return;
    try {
      const res = await api.get('/chat/conversation');
      if (!res.data?.success) return;
      setAgentNickname(res.data.agentNickname || '상담원');
      const conversation = res.data.conversation;
      if (conversation) {
        hadConversationRef.current = true;
        setClosedNotice(false);
        const next = res.data.messages || [];
        setMessages(prev => (isSameList(prev, next) ? prev : next));
        if (openRef.current && isPageVisible()) {
          // 창이 열려 있으면 즉시 읽음 처리
          if ((conversation.userUnread || 0) > 0) api.post('/chat/read').catch(() => {});
          setUnread(0);
        } else {
          setUnread(conversation.userUnread || 0);
        }
      } else {
        // 소켓 알림을 놓친 채 상담이 끝난 경우 — 말없이 비우지 않고 종료 안내를 남긴다
        if (hadConversationRef.current) {
          hadConversationRef.current = false;
          setClosedNotice(true);
          resetScrollMark();
        }
        setMessages(prev => (prev.length ? [] : prev));
        setUnread(0);
      }
    } catch (e) {
      // 운영 중 채팅이 꺼지면 버튼을 거둔다. 그 밖의 조회 실패는 다음 폴링에서 복구
      if (e.response?.data?.code === CHAT_DISABLED_CODE) hideChat();
    }
  }, [hideChat, resetScrollMark]);

  // 로그인 상태로 위젯이 뜨면 미확인 수 확보(배지)
  useEffect(() => { if (available && user) refresh(); }, [available, user, refresh]);

  // ── 소켓 수신 (uuid 병합) + 재연결 재조회 ──
  useEffect(() => {
    if (!available) return undefined;
    const onMessage = (e) => {
      const { message, agentNickname: nick } = e.detail || {};
      if (nick) setAgentNickname(nick);
      if (!message) return;
      hadConversationRef.current = true;
      setClosedNotice(false);
      setMessages(prev => (prev.some(m => m.client_uuid === message.client_uuid) ? prev : [...prev, message]));
      // 회원 소켓은 탭 전부가 받는다 — 내가 다른 탭에서 보낸 글은 안 읽은 수·읽음 대상이 아니다
      if (message.sender_type === 'user') return;
      if (openRef.current && isPageVisible()) {
        api.post('/chat/read').catch(() => {});
      } else {
        setUnread(prev => prev + 1);
      }
    };
    const onClosed = () => {
      // 종료: 유저 화면에서는 대화가 사라진다(내역은 관리자 전용). 안내는 회원이 창을 열어 볼 때까지 남긴다
      hadConversationRef.current = false;
      setClosedNotice(true);
      resetScrollMark();
      setMessages([]);
      setUnread(0);
    };
    const onRead = () => {
      setMessages(prev => prev.map(m => (m.sender_type === 'user' && !m.read_at ? { ...m, read_at: new Date().toISOString() } : m)));
    };
    const onReconnect = () => refresh(); // 끊김 동안 놓친 메시지·배지 복구
    window.addEventListener('chat:message', onMessage);
    window.addEventListener('chat:closed', onClosed);
    window.addEventListener('chat:read', onRead);
    window.addEventListener('socketConnected', onReconnect);
    return () => {
      window.removeEventListener('chat:message', onMessage);
      window.removeEventListener('chat:closed', onClosed);
      window.removeEventListener('chat:read', onRead);
      window.removeEventListener('socketConnected', onReconnect);
    };
  }, [available, refresh, resetScrollMark]);

  // ── 탭이 다시 보일 때 다시 읽는다 ──
  //   WHY: 다른 탭에서 읽어도 이 탭에는 알림이 오지 않아 숫자가 남는다. 창을 연 채 가려져 있던 동안 온 글도 여기서 읽음으로 보낸다(refresh).
  useEffect(() => {
    if (!available) return undefined;
    const onVisibility = () => { if (isPageVisible()) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [available, refresh]);

  // ── 창 열림 중 폴백 폴링(15초) — 내부 통지 유실 대비 ──
  useEffect(() => {
    if (!open || !user) return undefined;
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [open, user, refresh]);

  // ── 스크롤: 새 글이 왔을 때만, 회원이 맨 아래를 보고 있을 때만 내린다 ──
  //   WHY: 지난 대화를 읽는 중에 끌려 내려가면 읽을 수가 없다. 위로 올라가 있으면 "새 메시지" 표시만 띄운다.
  //   scrollIntoView 대신 대화 칸만 움직인다 — 페이지 전체가 함께 움직이면 뒤 화면이 밀린다.
  const scrollToLatest = useCallback(() => {
    const body = bodyRef.current;
    if (body) body.scrollTop = body.scrollHeight;
    stickToBottomRef.current = true;
    setHasNewBelow(false);
  }, []);

  const onBodyScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const nearBottom = body.scrollHeight - body.scrollTop - body.clientHeight < NEAR_BOTTOM_PX;
    stickToBottomRef.current = nearBottom;
    if (nearBottom) setHasNewBelow(false);
  }, []);

  useEffect(() => {
    const grew = messages.length > shownCountRef.current;
    shownCountRef.current = messages.length;
    if (!open || !grew) return;
    const latest = messages[messages.length - 1];
    if (stickToBottomRef.current || latest?.sender_type === 'user') scrollToLatest();
    else setHasNewBelow(true);
  }, [messages, open, scrollToLatest]);

  useEffect(() => { if (open) scrollToLatest(); }, [open, scrollToLatest]);

  // 그림이 늦게 그려지면 칸이 길어진다 — 맨 아래를 보던 중이면 따라 내려간다
  const onMediaLoaded = useCallback(() => {
    if (stickToBottomRef.current) scrollToLatest();
  }, [scrollToLatest]);

  const closePanel = useCallback(() => {
    setOpen(false);
    setClosedNotice(false);
  }, []);

  const togglePanel = () => {
    if (!user) { window.location.href = '/login'; return; }
    if (open) { closePanel(); return; }
    setOpen(true);
    refresh();
    api.post('/chat/read').catch(() => {});
    setUnread(0);
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problem = checkAttachment(file, attachmentRules);
    if (problem) {
      window.alert(problem);
      return;
    }
    setPendingFile(file);
  };

  const send = async () => {
    const text = input.trim();
    const file = pendingFile;
    if ((!text && !file) || sending) return;
    setSending(true);
    // 입력칸은 바로 비운다 — 종전엔 성공 뒤에 비워, 보내는 동안 친 다음 글까지 지워졌다. 실패하면 아래에서 되살린다
    setInput('');
    try {
      const previous = draftRef.current;
      const draft = previous && previous.text === text && previous.file === file
        ? previous
        : { text, file, uuid: genUuid(), attachment: previous && previous.file === file ? previous.attachment : null };
      draftRef.current = draft;

      if (file && !draft.attachment) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/chat/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: UPLOAD_TIMEOUT_MS,
          onUploadProgress: (event) => {
            if (event.total) setUploadPercent(Math.round((event.loaded / event.total) * 100));
          }
        });
        if (!up.data?.success) throw new Error(up.data?.error || '업로드 실패');
        draft.attachment = up.data.file;
      }

      const res = await api.post('/chat/messages', { content: text || undefined, clientUuid: draft.uuid, attachment: draft.attachment });
      if (!res.data?.success || !res.data.message) throw new Error(res.data?.error || '전송 실패');

      draftRef.current = null;
      hadConversationRef.current = true;
      setClosedNotice(false); // 새 상담이 시작됐으니 앞 상담의 종료 안내를 거둔다
      setMessages(prev => (prev.some(m => m.client_uuid === draft.uuid) ? prev : [...prev, res.data.message]));
      setPendingFile(current => (current === file ? null : current));
    } catch (e) {
      // 전송 실패: 보낸 글을 입력칸에 되살리고(그 사이 친 글은 뒤에 붙인다) 사유를 알린다
      if (text) setInput(current => (current ? `${text}\n${current}` : text));
      window.alert(e.response?.data?.error || (!e.response && e.message) || '메시지 전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      if (e.response?.data?.code === CHAT_DISABLED_CODE) hideChat();
    } finally {
      setSending(false);
      setUploadPercent(null);
    }
  };

  const onInputKeyDown = (e) => {
    // 한글 조합 중 Enter 는 글자 확정이다 — 여기서 보내면 마지막 글자가 입력칸에 남거나 두 번 간다
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const sendLabel = sending ? (uploadPercent !== null ? `전송 중 ${uploadPercent}%` : '전송 중') : '전송';

  return {
    available,
    attachmentRules,
    open,
    agentNickname,
    messages,
    unread,
    input,
    setInput,
    sending,
    sendLabel,
    pendingFile,
    clearPendingFile: () => setPendingFile(null),
    closedNotice,
    hasNewBelow,
    bodyRef,
    fileRef,
    onBodyScroll,
    scrollToLatest,
    onMediaLoaded,
    closePanel,
    togglePanel,
    pickFile,
    send,
    onInputKeyDown,
  };
};

export default useChatWidget;
